/**
 * Notification Store
 * ------------------
 * Manages the state for the Alert Notifications section in Settings.
 *
 * Covers two independent forms:
 *   - settings: alert preferences (recipients, delivery modes, thresholds, triggers)
 *   - smtp:     SMTP server configuration
 *
 * Both forms are loaded once when the Settings page mounts and saved
 * independently via their own save actions.
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import {
  getNotificationSettings,
  saveNotificationSettings,
  getSmtpSettings,
  saveSmtpSettings,
  sendTestEmail,
  DEFAULT_SETTINGS,
  DEFAULT_SMTP,
} from "../services/notificationService";

export const useNotificationStore = defineStore("notificationStore", () => {

  // ---------------------------------------------------------------------------
  // Settings form state
  // ---------------------------------------------------------------------------

  const settings = ref({ ...DEFAULT_SETTINGS });
  const availableTactics = ref([]);   // populated from GET, never sent to PUT
  const settingsLoading = ref(false);
  const settingsSaving  = ref(false);
  const settingsSaved   = ref(false);
  const settingsError   = ref(null);

  /**
   * Loads notification settings from the API and populates the form.
   * Also populates availableTactics from the same response.
   * Safe to call multiple times — only fetches if not already loading.
   */
  async function loadSettings() {
    if (settingsLoading.value) return;
    settingsLoading.value = true;
    settingsError.value   = null;
    try {
      const { settings: data, availableTactics: tactics } = await getNotificationSettings();
      settings.value        = data;
      availableTactics.value = tactics;
    } catch (err) {
      console.error("[notificationStore] loadSettings error:", err);
      settingsError.value = "Failed to load notification settings.";
    } finally {
      settingsLoading.value = false;
    }
  }

  /**
   * Saves the current settings form state to the API.
   */
  async function saveSettings() {
    if (settingsSaving.value) return;
    settingsSaving.value = true;
    settingsSaved.value  = false;
    settingsError.value  = null;
    try {
      await saveNotificationSettings(settings.value);
      settingsSaved.value = true;
      setTimeout(() => { settingsSaved.value = false; }, 3000);
    } catch (err) {
      console.error("[notificationStore] saveSettings error:", err);
      settingsError.value = err.message ?? "Failed to save settings.";
    } finally {
      settingsSaving.value = false;
    }
  }

  // ---------------------------------------------------------------------------
  // SMTP form state
  // ---------------------------------------------------------------------------

  const smtp = ref({ ...DEFAULT_SMTP });
  const smtpLoading = ref(false);
  const smtpSaving  = ref(false);
  const smtpSaved   = ref(false);   // true for 3s after successful save
  const smtpError   = ref(null);

  /**
   * Loads SMTP configuration from the API and populates the form.
   * The password field will be masked (e.g. "SG.abc***").
   */
  async function loadSmtp() {
    if (smtpLoading.value) return;
    smtpLoading.value = true;
    smtpError.value   = null;
    try {
      const data = await getSmtpSettings();
      smtp.value = data;
    } catch (err) {
      console.error("[notificationStore] loadSmtp error:", err);
      smtpError.value = "Failed to load SMTP settings.";
    } finally {
      smtpLoading.value = false;
    }
  }

  /**
   * Saves the current SMTP form state to the API.
   */
  async function saveSmtp() {
    if (smtpSaving.value) return;
    smtpSaving.value = true;
    smtpSaved.value  = false;
    smtpError.value  = null;
    try {
      await saveSmtpSettings(smtp.value);
      smtpSaved.value = true;
      setTimeout(() => { smtpSaved.value = false; }, 3000);
    } catch (err) {
      console.error("[notificationStore] saveSmtp error:", err);
      smtpError.value = err.message ?? "Failed to save SMTP settings.";
    } finally {
      smtpSaving.value = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Test email
  // ---------------------------------------------------------------------------

  const testSending = ref(false);
  const testResult  = ref(null);   // null | { sent: boolean, error?: string }

  /**
   * Sends a test email to the recipients currently in the settings form.
   * Uses the recipients from `settings.value.recipients` — not the saved ones.
   */
  async function runTestEmail() {
    if (testSending.value) return;
    testSending.value = true;
    testResult.value  = null;
    try {
      const result = await sendTestEmail(settings.value.recipients);
      testResult.value = result;
    } finally {
      testSending.value = false;
      setTimeout(() => { testResult.value = null; }, 5000);
    }
  }

  // ---------------------------------------------------------------------------
  // Recipients helpers
  // ---------------------------------------------------------------------------

  /**
   * Adds an email to the recipients list if valid and not already present.
   * Returns true if added, false if validation failed or already exists.
   *
   * @param {string} email
   * @returns {boolean}
   */
  function addRecipient(email) {
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) return false;
    if (settings.value.recipients.includes(clean)) return false;
    settings.value.recipients.push(clean);
    return true;
  }

  /**
   * Removes an email from the recipients list by index.
   *
   * @param {number} index
   */
  function removeRecipient(index) {
    settings.value.recipients.splice(index, 1);
  }

  // ---------------------------------------------------------------------------
  // Tactics helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns true if the given tactic ID is currently selected as a trigger.
   *
   * @param {string} tacticId - e.g. "TA0004"
   * @returns {boolean}
   */
  function isTacticActive(tacticId) {
    return (settings.value.tactics ?? []).includes(tacticId);
  }

  /**
   * Toggles a tactic ID in the tactics array.
   * Adds it if not present, removes it if already selected.
   *
   * @param {string} tacticId - e.g. "TA0004"
   */
  function toggleTactic(tacticId) {
    const current = settings.value.tactics ?? [];
    if (current.includes(tacticId)) {
      settings.value.tactics = current.filter(id => id !== tacticId);
    } else {
      settings.value.tactics = [...current, tacticId];
    }
  }

  // ---------------------------------------------------------------------------
  // Bootstrap — load both forms in parallel
  // ---------------------------------------------------------------------------

  /**
   * Loads settings and SMTP in parallel.
   * Call this once when the Settings page mounts.
   */
  async function init() {
    await Promise.all([loadSettings(), loadSmtp()]);
  }

  // ---------------------------------------------------------------------------
  // Expose
  // ---------------------------------------------------------------------------

  return {
    // Settings
    settings,
    availableTactics,
    settingsLoading,
    settingsSaving,
    settingsSaved,
    settingsError,
    loadSettings,
    saveSettings,

    // SMTP
    smtp,
    smtpLoading,
    smtpSaving,
    smtpSaved,
    smtpError,
    loadSmtp,
    saveSmtp,

    // Test email
    testSending,
    testResult,
    runTestEmail,

    // Recipients helpers
    addRecipient,
    removeRecipient,

    // Tactics helpers
    isTacticActive,
    toggleTactic,

    // Bootstrap
    init,
  };
});